import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Briefcase, Users } from 'lucide-react';
interface SlideScaleVsRelevanceProps {
  onContinue: () => void;
}
export function SlideScaleVsRelevance({
  onContinue
}: SlideScaleVsRelevanceProps) {
  const [stage, setStage] = useState(0);
  const agency = ['Too expensive for high campaign volumes', 'Too slow for frequent local adaptation', 'Too fragmented across brands and markets', 'Too dependent on repeated briefing cycles', 'Too difficult to measure across countries'];
  const internal = ['Capacity to get campaigns live in 20+ markets', 'Attention for legacy and low-priority brands', 'Per-market adaptation at campaign speed', 'Continuous optimization after launch', 'Execution that does not consume affiliate hours'];
  useEffect(() => {
    const timers = [300, 1000, 1700, 2500].map((ms, idx) => setTimeout(() => setStage(idx + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-12 max-w-7xl mx-auto" data-id="element-1088" data-mp="19kc620jd2ilc">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-3" data-id="element-1089" data-mp="14qzv2t7luab2">
        <p className="text-teal-400 font-semibold tracking-widest uppercase text-sm" data-id="element-1090" data-mp="uvptu7p166yc">
          The Trade-off
        </p>
        <h2 className="text-3xl md:text-5xl font-serif text-white" data-id="element-1091" data-mp="1sfsrsrw1wm6q">
          Production was solved. Getting it live was not.
        </h2>
        <p className="text-lg text-white/60" data-id="element-1092" data-mp="1ij6846fgo16a">
          Both models answer the supply question. Neither gets short, relevant
          campaigns in front of HCPs.
        </p>
      </motion.div>

      <div className="flex-1 flex flex-col justify-center max-w-6xl mx-auto w-full space-y-6" data-id="element-1093" data-mp="7528ajm6zitb">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1094" data-mp="32fn31k10w53">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: stage >= 1 ? 1 : 0,
          x: stage >= 1 ? 0 : -20
        }} className="bg-navy-900/50 border border-navy-700 rounded-2xl p-6 flex flex-col" data-id="element-1095" data-mp="351un16x3bnb">
            <div className="flex items-center gap-3 mb-4" data-id="element-1096" data-mp="3vpw6omo02bju">
              <div className="w-10 h-10 rounded-xl bg-rose-500/10 text-rose-400 flex items-center justify-center" data-id="element-1097" data-mp="3vsiemnesyt2l">
                <Briefcase className="w-5 h-5" data-id="element-1098" data-mp="3hx6ajperbq90" />
              </div>
              <h3 className="text-xl font-semibold text-white" data-id="element-1099" data-mp="3mraj2d3o86c7">
                The agency model
              </h3>
            </div>
            <p className="text-white/60 text-sm leading-relaxed mb-5" data-id="element-1100" data-mp="3czxohdqhn22h">
              Works well for a small number of major campaigns. At scale, it
              becomes:
            </p>
            <ul className="space-y-2.5 flex-1" data-id="element-1101" data-mp="2z2itzuldo43n">
              {agency.map((item, idx) => <motion.li key={item} initial={{
              opacity: 0,
              x: -8
            }} animate={{
              opacity: stage >= 2 ? 1 : 0,
              x: stage >= 2 ? 0 : -8
            }} transition={{
              delay: stage >= 2 ? idx * 0.08 : 0
            }} className="flex items-start gap-3 text-white/80 text-sm" data-id="element-1102" data-mp="333ul64jwgxdy" data-mpi={idx}>
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-400 mt-1.5 shrink-0" data-id="element-1103" data-mp="339z682js1qj3" />
                  {item}
                </motion.li>)}
            </ul>
            <p className="text-white/50 text-sm mt-5 pt-4 border-t border-navy-700 leading-relaxed" data-id="element-1104" data-mp="3c3uaz9gzley7">
              Difficult to scale across many brands, markets and campaign
              cycles.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: stage >= 3 ? 1 : 0,
          x: stage >= 3 ? 0 : 20
        }} className="bg-navy-900/50 border border-navy-700 rounded-2xl p-6 flex flex-col" data-id="element-1105" data-mp="3h3ytt65dalrl">
            <div className="flex items-center gap-3 mb-4" data-id="element-1106" data-mp="dtsbrh63yg8w">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 text-purple-500 flex items-center justify-center" data-id="element-1107" data-mp="iru64y8zi1zo">
                <Users className="w-5 h-5" data-id="element-1108" data-mp="3i7h0l2tjx55y" />
              </div>
              <h3 className="text-xl font-semibold text-white" data-id="element-1109" data-mp="3i91ucq81kk2r">
                The internalized model — LEO today
              </h3>
            </div>
            <p className="text-white/60 text-sm leading-relaxed mb-5" data-id="element-1110" data-mp="3r6m0acgun4jp">
              Solved creative supply, speed of production and cost per asset.
              What it does not solve:
            </p>
            <ul className="space-y-2.5 flex-1" data-id="element-1111" data-mp="16gd52n6592f3">
              {internal.map((item, idx) => <motion.li key={item} initial={{
              opacity: 0,
              x: 8
            }} animate={{
              opacity: stage >= 4 ? 1 : 0,
              x: stage >= 4 ? 0 : 8
            }} transition={{
              delay: stage >= 4 ? idx * 0.08 : 0
            }} className="flex items-start gap-3 text-white/80 text-sm" data-id="element-1112" data-mp="1bfgai8q1danl" data-mpi={idx}>
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-1.5 shrink-0" data-id="element-1113" data-mp="2d8txtt0hk04y" />
                  {item}
                </motion.li>)}
            </ul>
            <p className="text-white/50 text-sm mt-5 pt-4 border-t border-navy-700 leading-relaxed" data-id="element-1114" data-mp="23931dlsd7b28">
              Master content exists. Making it short and relevant — and getting
              it live everywhere — is the constraint.
            </p>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: stage >= 4 ? 1 : 0,
        y: stage >= 4 ? 0 : 12
      }} className="bg-rose-500/10 border border-rose-500/30 rounded-2xl px-7 py-5" data-id="element-1115" data-mp="3sp2jvthvicuj">
          <p className="text-white/90 text-lg leading-relaxed" data-id="element-1116" data-mp="1h1za777e2sev">
            Internalization solved the production question. The bottleneck moved
            downstream — to{' '}
            <span className="text-rose-400 font-semibold" data-id="element-1117" data-mp="29q4vpw69olfr">
              local execution capacity
            </span>
            . Master content that never goes live in 15 markets creates no
            impact.
          </p>
        </motion.div>
      </div>
    </div>;
}